import React, { useContext } from 'react';
import Typography from '@mui/material/Typography';
import classNames from 'classnames';
import { useI18nContext, Button } from '@procore/core-react';
import { type Project } from '../../types/project';
import { useStyles } from './PublicProjectBidDocuments.styles';
import AnalyticsWrappedComponent from '../Analytics/AnalyticsWrappedComponent';
import { formatDatetime } from '../../helpers/format-datetime';
import DocumentsIcon from '../Icons/DocumentsIcon';
import AppContext from '../AppContext/AppContext';
import { useLoginUrl } from '../../helpers/useLoginUrl';
import { getBidDueStatus } from '../../helpers/build-texts';

const PublicProjectBidDocuments = ({ project }: { project: Project }) => {
  const I18n = useI18nContext();
  const { isUserLoggedIn } = useContext(AppContext);
  const classes = useStyles();
  const loginUrl = useLoginUrl();

  const { biddingClosed } = getBidDueStatus(project);
  const BidPackageDocsDownloadUrl = `/api/bid-packages/public-bid-docs-manifest?bidPackageId=${project.bidPackageId}&companyId=${project.companyId}`;
  const link = isUserLoggedIn ? BidPackageDocsDownloadUrl : loginUrl;
  const buttonTexti18nKey = isUserLoggedIn
    ? 'pages.j.public_project_layout.public_project_bid_documents.download_latest_documents'
    : 'pages.j.public_project_layout.public_project_bid_documents.login_to_download_documents';

  return (
    <div className={classes.container}>
      <Typography variant="h2" className={classes.title}>
        {I18n.t(
          'pages.j.public_project_layout.public_project_bid_documents.bid_documents'
        )}
      </Typography>
      {project.bidDocuments.count > 0 && (
        <div className={classes.documents}>
          <div className={classes.downloadLatest}>
            {I18n.t(
              'pages.j.public_project_layout.public_project_bid_documents.download_latest'
            )}
          </div>
          <div className={classes.info}>
            <div className={classes.documentsLogo}>
              <DocumentsIcon />
            </div>
            <div className={classes.filesInfoAndLastUpdated}>
              <div className={classes.filesInfo}>
                {project.bidDocuments.count}{' '}
                {I18n.t(
                  'pages.j.public_project_layout.public_project_bid_documents.files'
                )}{' '}
                | {project.bidDocuments.size}
              </div>
              <div
                className={classes.lastUpdated}
                suppressHydrationWarning={true}
              >
                {I18n.t(
                  'pages.j.public_project_layout.public_project_bid_documents.last_updated'
                )}
                : {formatDatetime(project.bidDocuments.lastUpdateDate)}
              </div>
            </div>
          </div>
          <AnalyticsWrappedComponent
            componentType="a"
            eventText={`Clicked Download Latest Documents From Public Project Page ${
              !isUserLoggedIn ? '(Not Logged In)' : ''
            }`}
            eventLabel={`${
              project.bidPackagePrimarySlug
            }, CTA, Download Latest Documents ${
              !isUserLoggedIn ? '(Not Logged In)' : ''
            }`}
            aria-label="Download Latest Documents"
            className={classNames(
              classes.buttonContainer,
              biddingClosed ? classes.disableButtonContainer : ''
            )}
            data-track-click={`${
              project.bidPackagePrimarySlug
            }, CTA, Download Latest Documents ${
              !isUserLoggedIn ? '(Not Logged In)' : ''
            }`}
            href={link}
            target="_blank"
            rel="noreferrer"
            tabIndex={-1}
          >
            <Button
              className={classNames(classes.button, 'A11yException')}
              variant="secondary"
              disabled={biddingClosed}
            >
              {I18n.t(buttonTexti18nKey)}
            </Button>
          </AnalyticsWrappedComponent>
          {biddingClosed && (
            <div className={classes.biddingClosed}>
              {I18n.t(
                'pages.j.public_project_layout.public_project_bid_documents.bidding_has_closed'
              )}
            </div>
          )}
        </div>
      )}
      {project.bidDocuments.count === 0 && (
        <div className={classes.noDocumentsUploaded}>
          {I18n.t(
            'pages.j.public_project_layout.public_project_bid_documents.no_documents_uploaded'
          )}
        </div>
      )}
    </div>
  );
};

export default PublicProjectBidDocuments;
